/**
 * 通用工具函数
 */
import { getI18n } from './i18n';
import { message } from '@ui';

export function formatDateTime(dateStr: string | number | Date | null | undefined): string {
  if (!dateStr) return '-';
  const i18n = getI18n();
  const locale = i18n?.getLanguage?.() ?? 'zh-CN';
  return new Date(dateStr).toLocaleString(locale);
}

export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'] as const;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const idx = Math.min(i, sizes.length - 1);
  return `${(bytes / Math.pow(k, idx)).toFixed(2)} ${sizes[idx]}`;
}

export async function copyToClipboard(text: string): Promise<void> {
  const tr = (key: string, fallback: string): string => {
    const i18n = getI18n();
    return i18n ? i18n.t(key) : fallback;
  };
  try {
    await navigator.clipboard.writeText(text);
    message?.success(tr('common.copiedToClipboard', 'Copied to clipboard'));
  } catch (err) {
    console.error('Copy failed:', err);
    message?.error(tr('common.copyFailed', 'Copy failed'));
  }
}

export function downloadFile(content: BlobPart, filename: string, type = 'text/plain'): void {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function debounce<T extends (...args: never[]) => void>(func: T, wait: number): T {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  return function (this: unknown, ...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  } as T;
}

export function throttle<T extends (...args: never[]) => void>(func: T, limit: number): T {
  let inThrottle = false;
  return function (this: unknown, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  } as T;
}

export const UtilsModule = {
  formatDateTime,
  formatDuration,
  formatBytes,
  copyToClipboard,
  downloadFile,
  debounce,
  throttle,
};

// 兼容层
declare global {
  interface Window {
    UtilsModule?: typeof UtilsModule;
  }
}
window.UtilsModule = UtilsModule;
